import { FC } from "react";
import styled from "styled-components";
import css from "@styled-system/css";
import NumberFormat, { NumberFormatValues } from "react-number-format";

import Icon from "../Icon/Icon";
import Typography from "../Typography/Typography";

export interface PriceInputProps {
  /**
   * The value attribute of the input component
   */
  value: number | undefined;
  /**
   * onChange callback
   */
  onChange: (value: number | undefined) => void;
  /**
   *The element's unique identifier
   */
  id?: string;
  /**
   * Step value
   */
  step?: number;
  /**
   * Disable decrementing
   * */
  decrementDisabled?: boolean;
  /**
   * Disable incrementing
   */
  incrementDisabled?: boolean;
  /**
   * Enable the invalid state of the input component
   */
  invalidInput?: boolean;
  /**
   * Custom styling
   */
  css?: string;
  /**
   * Show currency besides the value
   */
  showCurrency?: boolean;
  /**
   * The min attribute of the input component
   */
  minimumInputValue?: number;
  /**
   * The max attribute of the input component
   */
  maximumInputValue?: number;
  /**
   * Allow decimals
   */
  allowDecimalNumbers?: boolean;
  /**
   * Number of decimals. Default value: 1 (if allowDecimalNumber is true)
   */
  numberOfDecimals?: number;
  /**
   * Align text to center
   */
  centred?: boolean;
  /**
   * Display number sign (+ for positive numbers, - for negative numbers)
   */
  displayNumberSign?: boolean;
}

const StyledInputContainer = styled.div<{ invalidInput?: boolean; css?: any }>`
  display: flex;
  position: relative;

  border-radius: 8px;
  ${(props) =>
    css({
      width: "100%",
      height: "48px",
      backgroundColor: "background-default-color",
      boxShadow: "default",
      border: props.invalidInput ? "warningBorder" : "none",
    })}
  ${(props) => props.css}
`;

const StyledButton = styled.button`
  width: 48px;
  border: none;
  z-index: 1;
  padding: 0;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  &[disabled] {
    opacity: 0.3;
  }
  :first-of-type {
    border-radius: 8px 0 0 8px;
  }
  :last-of-type {
    border-radius: 0 8px 8px 0;
  }
  :focus {
    outline: none;
  }
  ${css({
    backgroundColor: "background-default-color",
    color: "cta-tertiary-text-color-default",
    "&:focus": {
      border: "primaryBorder",
    },
  })}
`;

const StyledInput = styled(NumberFormat)<{
  $showCurrency?: boolean;
  $centred?: boolean;
}>`
  border: none;
  border-radius: 0;
  flex: 1;
  min-width: 0;
  padding: 0;
  padding-left: 28px;
  margin: 0;
  text-align: left;
  box-shadow: none;
  border-left: 1px solid rgba(230, 235, 240, 0.78);
  border-right: 1px solid rgba(230, 235, 240, 0.78);

  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    display: none;
  }
  -moz-appearance: textfield;
  ${(props) =>
    css({
      color: "grey-blue",
      lineHeight: 1,
      fontFamily: "normal",
      fontSize: "h6",
      fontWeight: "semiBold",
      textAlign: props.$centred ? "center" : "left",
      paddingLeft: props.$showCurrency ? 7 : 5,
      "&:focus": {
        outline: "none",
        border: "primaryBorder",
      },
    })}
`;

const Minus = styled.div`
  width: 18px;
  height: 3px;
  pointer-events: none;
  ${css({
    backgroundColor: "cta-tertiary-text-color-default",
  })}
`;

const ScreenReadersSpan = styled.span`
  :not(:focus),
  :not(:active) {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }
`;

const CurrencySymbol = styled(Typography)`
  position: absolute;
  font-weight: 500;
  opacity: 0.5;
  top: -23px;
  left: 64px;
`;

const PriceInput: FC<PriceInputProps> = ({
  value,
  step = 100,
  decrementDisabled = false,
  incrementDisabled = false,
  invalidInput = false,
  onChange,
  showCurrency = true,
  minimumInputValue = 100,
  maximumInputValue,
  allowDecimalNumbers = true,
  numberOfDecimals = 0,
  centred = false,
  displayNumberSign,
  ...props
}) => {
  const prefix =
    displayNumberSign && value !== undefined && value > 0 ? "+" : undefined;

  const increment = () => {
    const newValue = value !== undefined ? value + step : minimumInputValue;
    const valueToUpdate =
      maximumInputValue && newValue > maximumInputValue
        ? maximumInputValue
        : newValue;

    onChange(valueToUpdate);
  };

  const decrement = () => {
    const newValue = value !== undefined ? value - step : minimumInputValue;
    const valueToUpdate =
      newValue < minimumInputValue ? minimumInputValue : newValue;

    onChange(valueToUpdate);
  };

  const isMinValue = (value: number) => {
    return value <= minimumInputValue;
  };

  const isMaxValue = (value: number) => {
    return maximumInputValue && value >= maximumInputValue;
  };

  const onValueChange = (newValues: NumberFormatValues) => {
    const newValue = newValues.floatValue;

    onChange(newValue);
  };

  const onBlur = () => {
    const newValue = minimumInputValue < 0 ? 0 : minimumInputValue;

    if (value === undefined) {
      onChange(newValue);
    } else {
      if (value < minimumInputValue) {
        onChange(minimumInputValue);
        return;
      }

      if (maximumInputValue !== undefined && value > maximumInputValue) {
        onChange(maximumInputValue);
        return;
      }
    }
  };

  return (
    <StyledInputContainer
      data-testid="price-input-container"
      invalidInput={invalidInput}
      {...props}
      onBlur={onBlur}
    >
      <StyledButton
        onClick={decrement}
        disabled={value === undefined || isMinValue(value) || decrementDisabled}
        data-en="click"
        data-ec="button"
        data-el="pricing"
        data-ea="navigate"
        data-es=""
        aria-label="decrease"
      >
        <Minus />
        <ScreenReadersSpan>-</ScreenReadersSpan>
      </StyledButton>
      {showCurrency && (
        <CurrencySymbol variant="h6" color="grey-blue">
          &pound;
        </CurrencySymbol>
      )}
      <StyledInput
        $showCurrency={showCurrency}
        $centred={centred}
        value={value}
        prefix={prefix}
        decimalSeparator="."
        displayType="input"
        type="text"
        allowNegative={true}
        decimalScale={allowDecimalNumbers ? numberOfDecimals : 0}
        fixedDecimalScale={allowDecimalNumbers}
        onValueChange={onValueChange}
        min={minimumInputValue}
        max={maximumInputValue}
        aria-label="bid-price"
      />
      <StyledButton
        onClick={increment}
        disabled={value === undefined || isMaxValue(value) || incrementDisabled}
        data-en="click"
        data-ec="button"
        data-el="pricing"
        data-ea="navigate"
        data-es=""
        aria-label="increase"
      >
        <Icon
          name="plus"
          color="primary-light-blue"
          width="24px"
          height="24px"
          css="pointer-events: none;"
        />
        <ScreenReadersSpan>+</ScreenReadersSpan>
      </StyledButton>
    </StyledInputContainer>
  );
};

export default PriceInput;
